import { useParams, useNavigate } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { getStepDisplayName } from "@/lib/stepUtils";
import { AppHeader } from "@/components/AppHeader";
import {
    ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from "@/components/ui/resizable";
import { ReactionCanvas } from "@/components/step/ReactionCanvas";
import { StepDetailsPanel } from "@/components/step/StepDetailsPanel";
import { VariantTable } from "@/components/step/VariantTable";
import { useEffect, useState, useCallback } from "react";
import type { SynthesisStep, StepVariant, Chemical } from "@/types/chemistry";
import { KetcherEditor } from "@/components/step/KetcherEditor";
import { initRDKit } from "@/components/step/chemistry/utils/initRDKit";
import { parseRxnFileRoles, parseSdfFile } from "@/components/step/chemistry/utils/handleReactions";
import { JSMol } from "@/types/rdkit";
import { getConnectedReactant, getConnectedProduct } from "@/lib/stepConnectivity";


const ATOMIC_SYMBOLS = {
    1: 'H', 5: 'B', 6: 'C', 7: 'N', 8: 'O', 9: 'F', 11: 'Na', 12: 'Mg',
    14: 'Si', 15: 'P', 16: 'S', 17: 'Cl', 19: 'K', 20: 'Ca', 26: 'Fe',
    29: 'Cu', 30: 'Zn', 33: 'As', 34: 'Se', 35: 'Br', 53: 'I',
    // extend as needed
};

function getMolFormula(mol) {
    const json = JSON.parse(mol.get_json());
    const defaults = json.defaults?.atom || {};
    const defaultZ = defaults.z ?? 6;
    const defaultImpHs = defaults.impHs ?? 0;

    const atoms = json.molecules?.[0]?.atoms || [];
    const counts = {};

    for (const atom of atoms) {
        const z = atom.z ?? defaultZ;
        const impHs = atom.impHs ?? defaultImpHs;
        const symbol = ATOMIC_SYMBOLS[z] || `[#${z}]`;

        counts[symbol] = (counts[symbol] || 0) + 1;

        if (impHs > 0) {
            counts["H"] = (counts["H"] || 0) + impHs;
        }
    }

    // Hill system ordering: C first, H second, then alphabetical
    const symbols = Object.keys(counts).sort((a, b) => {
        if (a === "C") return -1;
        if (b === "C") return 1;
        if (a === "H") return -1;
        if (b === "H") return 1;
        return a.localeCompare(b);
    });

    return symbols
        .map((s) => (counts[s] > 1 ? `${s}${counts[s]}` : s))
        .join("");
}


function getChemicalDetails(mol: JSMol): Chemical {
    const descriptors = JSON.parse(mol.get_descriptors());
    return {
        id: window.RDKit.get_inchikey_for_inchi(mol.get_inchi()),
        name: "",
        formula: getMolFormula(mol),
        casNumber: descriptors.casNumber || "",
        molfile: mol.get_v3Kmolblock(),
        smiles: mol.get_smiles(),
        molarWeight: descriptors.amw,
        earliestDate: "",
        price: 0,
        co2eq: 0,
    };
}

const StepDetail = () => {
    const { projectId, stepId } = useParams<{ projectId: string; stepId: string }>();
    const navigate = useNavigate();
    const {
        getProject, updateStep, addChemical, updateChemical, removeChemical,
        addVariant, updateVariant, deleteVariant, setActiveVariant,
    } = useProjects();
    const project = getProject(projectId!);
    const step = project?.steps.find((s) => s.id === stepId);

    useEffect(() => {
        initRDKit();
    }, []);

    // Auto-populate linked chemicals from adjacent steps
    useEffect(() => {
        if (!project || !step) return;

        const reactantResult = getConnectedReactant(step, project.steps);
        if (reactantResult?.type === "auto") {
            const alreadyLinked = step.reactants.some(
                (c) => c.linkedStepId === reactantResult.fromStep.id
            );
            if (!alreadyLinked) {
                const linked: Chemical = { ...reactantResult.chemical, linkedStepId: reactantResult.fromStep.id };
                updateStep(project.id, step.id, {
                    reactants: [linked, ...step.reactants.filter(c => !c.linkedStepId)],
                });
            }
        }

        const productResult = getConnectedProduct(step, project.steps);
        if (productResult?.type === "auto") {
            const alreadyLinked = step.products.some(
                (c) => c.linkedStepId === productResult.fromStep.id
            );
            if (!alreadyLinked) {
                const linked: Chemical = { ...productResult.chemical, linkedStepId: productResult.fromStep.id };
                updateStep(project.id, step.id, {
                    products: [linked, ...step.products.filter(c => !c.linkedStepId)],
                });
            }
        }
    }, [project?.id, step?.id, project?.steps]);

    const variants = step?.variants || [];

    // Default to activeVariantId or first variant
    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(
        step?.activeVariantId || variants[0]?.id
    );

    if (!project || !step) {
        return (
            <div className="min-h-screen bg-background">
                <AppHeader />
                <div className="flex items-center justify-center py-20">
                    <p className="text-muted-foreground">Step not found</p>
                </div>
            </div>
        );
    }

    const activeVariant = selectedVariantId
        ? variants.find((v) => v.id === selectedVariantId)
        : variants[0];

    // Build a SynthesisStep-shaped object from the variant for reuse in panels
    const displayStep: SynthesisStep = activeVariant
        ? { ...step, ...activeVariant, id: step.id, label: step.label, variants: step.variants || [], activeVariantId: step.activeVariantId }
        : step;

    const handleUpdateDisplayStep = (updates: Partial<SynthesisStep>) => {
        if (activeVariant) {
            updateVariant(project.id, step.id, activeVariant.id, updates as Partial<StepVariant>);
        } else {
            updateStep(project.id, step.id, updates);
        }
    };

    const handleAddVariant = () => {
        addVariant(project.id, step.id);
    };

    const handleSelectVariant = (variantId: string) => {
        setSelectedVariantId(variantId);
        setActiveVariant(project.id, step.id, variantId);
    };

    const handleDeleteVariant = (variantId: string) => {
        // Don't allow deleting the last variant
        if (variants.length <= 1) return;
        if (selectedVariantId === variantId) {
            const remaining = variants.filter(v => v.id !== variantId);
            setSelectedVariantId(remaining[0]?.id);
        }
        deleteVariant(project.id, step.id, variantId);
    };

    const handleDrawingUpdate = async (_update: any, ketcher: any) => {
        if (ketcher.containsReaction()) {
            const rxn: string = await ketcher.getRxn('v3000');
            if (!rxn) return;
            if (rxn === step.reactionString) return;

            const {
                reactants,
                products,
                reagents,
            } = parseRxnFileRoles(rxn, window.RDKit);

            updateStep(project.id, step.id, {
                reactionString: rxn,
                reactants: reactants.map(r => getChemicalDetails(r)),
                products: products.map(p => getChemicalDetails(p)),
                reagents: reagents.map(r => getChemicalDetails(r))
            });
        } else {
            const mol: string = await ketcher.getSdf('v3000');
            if (!mol) return;
            if (mol === step.reactionString) return;

            const molecules = parseSdfFile(mol, window.RDKit);

            updateStep(project.id, step.id, {
                reactionString: mol,
                reactants: molecules.map(m => getChemicalDetails(m)),
            });
        }
    }

    return (
        <div className="flex min-h-screen flex-col bg-background">
            <AppHeader />
            {/* Breadcrumb bar */}
            <div className="px-6 py-2 text-sm text-muted-foreground border-b bg-card">
                <span
                    className="hover:text-foreground cursor-pointer"
                    onClick={() => navigate("/")}
                >
                    Synthesis Plans
                </span>
                <span className="mx-1.5">/</span>
                <span
                    className="hover:text-foreground cursor-pointer"
                    onClick={() => navigate(`/project/${project.id}`)}
                >
                    {project.name}
                </span>
                <span className="mx-1.5">/</span>
                <span className="text-primary font-medium">{getStepDisplayName(step)}</span>
            </div>

            <div className="flex-1">
                <ResizablePanelGroup direction="horizontal">
                    <ResizablePanel defaultSize={58} minSize={30}>
                        <div className="flex flex-col h-full">
                            <div className="flex-1">
                                <KetcherEditor ketFile={step.reactionString} onUpdate={handleDrawingUpdate} />
                            </div>
                            <div className="border-t px-4 py-3 bg-card">
                                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Notes</label>
                                <textarea
                                    className="w-full min-h-[80px] max-h-[160px] resize-y bg-transparent px-0 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none"
                                    placeholder="Add notes about this step…"
                                    value={step.notes || ""}
                                    onChange={(e) => updateStep(project.id, step.id, { notes: e.target.value })}
                                />
                            </div>
                        </div>
                    </ResizablePanel>
                    <ResizableHandle />
                    <ResizablePanel defaultSize={42} minSize={30}>
                        <div className="border-l h-full">
                            <StepDetailsPanel
                                step={displayStep}
                                projectId={project.id}
                                allSteps={project.steps}
                                onUpdateStep={handleUpdateDisplayStep}
                                onAddChemical={(type, data) => addChemical(project.id, step.id, type, data)}
                                onUpdateChemical={(type, chemId, updates) => updateChemical(project.id, step.id, type, chemId, updates)}
                                onRemoveChemical={(type, chemId) => removeChemical(project.id, step.id, type, chemId)}
                                onLinkChemical={(type, chemical, fromStepId) => {
                                    const linked: Chemical = { ...chemical, linkedStepId: fromStepId };
                                    const existing = step[type].filter(c => c.linkedStepId !== fromStepId);
                                    updateStep(project.id, step.id, {
                                        [type]: [linked, ...existing],
                                    });
                                }}
                                onViewBom={() => navigate(`/project/${project.id}/bom`)}
                            />
                        </div>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
        </div>
    );
};

export default StepDetail;
